import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { ComplianceByCategory } from "../../../types/contractDashboard";
import styles from "./contractComplianceByService.module.scss";

interface ContractComplianceByServiceProps {
  data: ComplianceByCategory[];
}

const ContractComplianceByService: React.FC<ContractComplianceByServiceProps> = ({ data }) => {
  // Memoize the formatted chart data
  const chartData = useMemo(() => {
    return data.map((item) => ({
      category: item.category,
      compliance: item.complianceRate,
      leakage: item.leakageRate,
      status: item.status,
    }));
  }, [data]);

  // Get status icon based on status string
  const getStatusIcon = (status: string) => {
    if (status === "good")
      return <CheckCircleIcon className={styles.statusIcon} style={{ color: "#10b981" }} />;
    if (status === "warning")
      return <WarningIcon className={styles.statusIcon} style={{ color: "#f59e0b" }} />;
    return <ErrorIcon className={styles.statusIcon} style={{ color: "#ef4444" }} />;
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box className={styles.tooltip}>
          <Box className={styles.tooltipHeader}>
            <Typography variant="body2" className={styles.tooltipCategory}>
              {data.category}
            </Typography>
            {getStatusIcon(data.status)}
          </Box>
          <Box className={styles.tooltipContent}>
            <Box className={styles.tooltipRow}>
              <Box className={styles.tooltipDot} style={{ background: "#10b981" }} />
              <Typography variant="body2" className={styles.tooltipLabel}>
                Compliance:
              </Typography>
              <Typography variant="body2" className={styles.tooltipValue}>
                {data.compliance}%
              </Typography>
            </Box>
            <Box className={styles.tooltipRow}>
              <Box className={styles.tooltipDot} style={{ background: "#ef4444" }} />
              <Typography variant="body2" className={styles.tooltipLabel}>
                Leakage:
              </Typography>
              <Typography variant="body2" className={styles.tooltipValue}>
                {data.leakage}%
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Custom legend
  const renderLegend = () => {
    return (
      <Box className={styles.legend}>
        <Box className={styles.legendItem}>
          <Box className={styles.legendDot} style={{ background: "#10b981" }} />
          <Typography variant="body2" className={styles.legendLabel}>
            Compliance Rate
          </Typography>
        </Box>
        <Box className={styles.legendItem}>
          <Box className={styles.legendDot} style={{ background: "#ef4444" }} />
          <Typography variant="body2" className={styles.legendLabel}>
            Leakage Rate
          </Typography>
        </Box>
      </Box>
    );
  };

  // Truncate long category names for Y-axis
  const formatYAxis = (value: string) => {
    return value.length > 15 ? `${value.substring(0, 15)}...` : value;
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Contract Compliance Rate
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            % of spend adhering to negotiated contract terms.
          </Typography>
        </Box>
        <Box className={styles.emptyState}>
          <Typography variant="body2" className={styles.emptyText}>
            No compliance data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Contract Compliance Rate
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          % of spend adhering to negotiated contract terms.
        </Typography>
      </Box>

      {renderLegend()}

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
            barCategoryGap="20%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e0e0e0"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              tick={{ fill: "#8ea39a", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="category"
              tick={{ fill: "#666666", fontSize: 13 }}
              width={120}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
              tickFormatter={formatYAxis}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f5f5f5" }} />
            <Bar dataKey="compliance" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
            <Bar dataKey="leakage" stackId="a" fill="#ef4444" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(ContractComplianceByService);
